// Lightweight, dependency-free preprocessing for photographed documents
// before OCR. Deliberately grayscale + contrast-stretch only, never hard
// binarize/threshold: Tesseract's LSTM engine (what this app uses) is
// documented to perform worse on hard-binarized images than on well
// -contrasted grayscale ones - binarization was tuned for the older legacy
// engine, not the LSTM one.

export interface PixelBuffer {
    data: Uint8ClampedArray;
    width: number;
    height: number;
}

// Standard luma weights (ITU-R BT.601), the same ones used by most image
// libraries for a perceptually reasonable grayscale conversion.
const LUMA_R = 0.299;
const LUMA_G = 0.587;
const LUMA_B = 0.114;

// Converts to grayscale, then linearly stretches the result so the darkest
// pixel becomes black and the lightest becomes white. Real-world photos
// (uneven lighting, shadows, a soft paper surface) often only use a narrow
// band of the available range - stretching that band back out to full
// contrast is one of the most reliable, cheap wins for OCR accuracy on
// photographed (rather than flat-scanned) documents.
export function grayscaleAndStretchContrast(image: PixelBuffer): PixelBuffer {
    const { data, width, height } = image;
    const pixelCount = width * height;
    const gray = new Float32Array(pixelCount);

    let min = 255;
    let max = 0;
    for (let i = 0; i < pixelCount; i++) {
        const offset = i * 4;
        const value = LUMA_R * data[offset] + LUMA_G * data[offset + 1] + LUMA_B * data[offset + 2];
        gray[i] = value;
        if (value < min) min = value;
        if (value > max) max = value;
    }

    const output = new Uint8ClampedArray(data.length);
    // A flat/blank image (min === max) has nothing to stretch - copy the
    // grayscale values through unchanged rather than dividing by zero.
    const range = max - min;
    for (let i = 0; i < pixelCount; i++) {
        const stretched = range === 0 ? gray[i] : ((gray[i] - min) / range) * 255;
        const offset = i * 4;
        output[offset] = stretched;
        output[offset + 1] = stretched;
        output[offset + 2] = stretched;
        output[offset + 3] = data[offset + 3];
    }

    return { data: output, width, height };
}
